import { addDays } from '@painpal/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { sessionQueryOptions } from '../../auth/session';
import { dismissalKey, keepCurrent, type IsDismissed } from './recommendationModel';

// Per user: two people can share a browser, and two users with no rides and no plans get the same basis.
export const dismissalsKey = (userId: string): string => `painpal.recommendations.dismissed.${userId}`;

// Storage can be missing or refuse access or writes (private mode, blocked site data, a full quota). Dismissals
// then live here, so they still last for the visit across page changes, until a reload or sign-out.
const unstored = new Map<string, readonly string[]>();

export function forgetUnstoredDismissals(): void {
  unstored.clear();
}

function parse(raw: string | null): string[] {
  try {
    const parsed: unknown = JSON.parse(raw ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    return [];
  }
}

function readStored(userId: string | null): readonly string[] {
  if (userId === null) return [];
  const held = unstored.get(userId) ?? [];
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(dismissalsKey(userId));
  } catch {
    return held;
  }
  return [...new Set([...parse(raw), ...held])];
}

function writeStored(userId: string | null, keys: readonly string[]): void {
  if (userId === null) return;
  try {
    window.localStorage.setItem(dismissalsKey(userId), JSON.stringify(keys));
    unstored.delete(userId);
  } catch {
    unstored.set(userId, keys);
  }
}

// The authed route has loaded the session before any page that dismisses renders, so this only reads it. With no
// user (the cache was just cleared on the way to sign in) a dismissal lasts only while the page stays mounted.
function useSignedInId(): string | null {
  return useQuery({ ...sessionQueryOptions(), enabled: false }).data?.id ?? null;
}

export interface Dismissals { isDismissed: IsDismissed; dismiss: (date: string, basis: string) => void }

// today is the browser's; the profile zone's today, which pages dismiss under, can be a day behind it, so pruning keeps that day too.
export function useDismissals(today: string): Dismissals {
  const userId = useSignedInId();
  const [held, setHeld] = useState(() => ({ userId, keys: readStored(userId) }));
  const keys = held.userId === userId ? held.keys : readStored(userId);
  if (held.userId !== userId) setHeld({ userId, keys });
  return {
    isDismissed: (date, basis) => keys.includes(dismissalKey(date, basis)),
    dismiss: (date, basis) => {
      const next = [...keepCurrent(keys, addDays(today, -1)), dismissalKey(date, basis)];
      setHeld({ userId, keys: next });
      writeStored(userId, next);
    },
  };
}
